import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react"
import { useQueryClient } from '@tanstack/react-query'
import { apiClient, tokenStorage } from '@/shared/services/client'
import type { User, AuthState, LoginCredentials } from '@/shared/types/auth'

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>
  logout: () => void
  checkAuth: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  })

  const fetchUser = useCallback(async (): Promise<User | null> => {
    try {
      const response = await apiClient.get<{ user: User; permissions: string[]; areas: User['areas'] }>("/auth/me")
      // El endpoint devuelve { user: {...}, permissions: [...], areas: [...] }
      return {
        ...response.data.user,
        permissions: response.data.permissions,
        areas: response.data.areas || [],
      }
    } catch {
      return null
    }
  }, [])

  const checkAuth = useCallback(async (): Promise<boolean> => {
    const token = tokenStorage.getAccessToken()

    if (!token) {
      // Intentar obtener JWT desde sesión Django activa (login legacy)
      try {
        const sessionResponse = await apiClient.post("/auth/session-to-jwt/")
        const { access, refresh } = sessionResponse.data
        tokenStorage.setTokens(access, refresh)
      } catch {
        setState({ user: null, isAuthenticated: false, isLoading: false })
        return false
      }
    }

    const user = await fetchUser()

    if (user) {
      setState({ user, isAuthenticated: true, isLoading: false })
      return true
    } else {
      tokenStorage.clearTokens()
      setState({ user: null, isAuthenticated: false, isLoading: false })
      return false
    }
  }, [fetchUser])

  const login = useCallback(async (credentials: LoginCredentials) => {
    const response = await apiClient.post("/auth/login/", credentials)
    const { access, refresh } = response.data

    tokenStorage.setTokens(access, refresh)

    const user = await fetchUser()

    if (!user) {
      tokenStorage.clearTokens()
      throw new Error("No se pudo obtener la información del usuario")
    }

    setState({ user, isAuthenticated: true, isLoading: false })
  }, [fetchUser])

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout/")
    } catch {
      // Continuar con logout local aunque falle el servidor
    }
    queryClient.clear()
    tokenStorage.clearTokens()
    sessionStorage.removeItem('password_modal_shown')
    sessionStorage.removeItem('phone_modal_shown')
    localStorage.removeItem('tonal-selected-team')
    localStorage.removeItem('tonal-selected-area-id')
    setState({ user: null, isAuthenticated: false, isLoading: false })
  }, [queryClient])

  // Check auth on mount
  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  // Listen for storage changes (logout from another tab or token cleared by interceptor)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'tonal_access_token' && !e.newValue) {
        setState({ user: null, isAuthenticated: false, isLoading: false })
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider")
  }

  return context
}
